import { datadogLogs } from '@datadog/browser-logs';
import React, { Component } from 'react';
import { inject, observer, disposeOnUnmount } from 'mobx-react';
import { reaction } from 'mobx';
import PropTypes from 'prop-types';

import './MainScreen.scss';

import LoaderComponent from 'components/loader/LoaderComponent';
import LivePaymentPageComponent from './components/live_payment_page/LivePaymentPageComponent';
import CardDeviceDataCollectionComponent from './components/card_device_data_collection_page/CardDeviceDataCollectionComponent';
import RobinsonsBankPaymentPageComponent from './components/robinsons_bank_payment_page/RobinsonsBankPaymentPageComponent';
import SathapanaBankPaymentPageComponent from './components/sathapana_bank_payment_page/SathapanaBankPaymentPageComponent';
import TestPaymentPageComponent from './components/test_payment_page/TestPaymentPageComponent';
import {
  isLegacyPartnersCallback,
  isPaymentsAppsCallback,
  redirectToPaymentsAppsCallback
} from '../../utils/shopifyRedirect';

import { CARD_PAYMENT_METHODS } from '../../constants/CardPaymentMethod';
import paymongoImage from '../../assets/paymongo-new.svg';

class MainScreen extends Component {
  componentDidMount() {
    // Used to bubble up the message after a successful MPGS 3DS authentication
    window.addEventListener(
      'message',
      ev => {
        if (ev.data === '3DS-authentication-complete') {
          // TODO: ENG-7758 Refactor iframe handling for test sources web
          // TODO: Uncomment logs once issue on constant checking of 3DS-authentication-complete has been resolved
          // datadogLogs.logger.info(
          //   'Notify organization of complete payment',
          //   {
          //     data: {
          //       callback_type: 'bubbled up event',
          //       notification_type: 'frontend callback',
          //     }
          //   }
          // );
          window.parent.postMessage('3DS-authentication-complete', '*');
        }
      },
      false
    );

    disposeOnUnmount(this, [
      reaction(
        () => {
          const { store } = this.props;

          return store.parameterId;
        },
        id => {
          if (id !== '<none>' && id !== undefined) {
            const { store } = this.props;

            store.loadData();
          }
        }
      ),
      reaction(
        () => {
          const { store } = this.props;

          return store.redirectUrl;
        },
        redirectUrl => {
          const { store } = this.props;

          if (redirectUrl !== undefined) {
            if (redirectUrl === '') {
              store.setPageLoading(true);
              window.location.reload();
            } else if (store.willRedirectToNewPage) {
              datadogLogs.logger.info('Notify organization of complete payment', {
                data: {
                  notification_type: 'redirect to return URL',
                  redirect_url: redirectUrl,
                  source_id: store.source.id
                }
              });

              store.setPageLoading(true);

              if (isPaymentsAppsCallback(redirectUrl)) {
                redirectToPaymentsAppsCallback(redirectUrl, { source_id: store.source.id });
              } else if (isLegacyPartnersCallback(redirectUrl)) {
                if (window.top !== window.parent) {
                  // Skip redirect for Shopify checkout iframe
                  store.setPageLoading(false);
                } else {
                  window.top.location.href = redirectUrl;
                }
              } else {
                window.location.href = redirectUrl;
              }
            }
          }
        }
      ),
      reaction(
        () => {
          const { store } = this.props;

          return store.source.status;
        },
        sourceStatus => {
          const { store } = this.props;

          if (sourceStatus !== 'pending') {
            store.setPageLoading(false);
          }

          if ([...CARD_PAYMENT_METHODS, 'three_d_secure'].includes(store.source.type)) {
            if (store.willPostMessageToWindow) {
              if (window.top !== window.parent) {
                datadogLogs.logger.info('Notify organization of complete payment', {
                  data: {
                    callback_type: 'iframe top not same as parent',
                    notification_type: 'frontend callback',
                    source_id: store.source.id
                  }
                });

                window.parent.postMessage('3DS-authentication-complete', '*');
              } else {
                datadogLogs.logger.info('Notify organization of complete payment', {
                  data: {
                    notification_type: 'frontend callback',
                    source_id: store.source.id
                  }
                });

                window.top.postMessage('3DS-authentication-complete', '*');
              }
            }
          }

          if (['atome', 'billease', 'paymaya'].includes(store.source.type)) {
            if (sourceStatus === 'consumed') {
              window.location.href = store.source.successUrl;
            } else if (sourceStatus === 'failed') {
              window.location.href = store.source.failedUrl;
            }
          }
        }
      )
    ]);

    const { store } = this.props;
    const search = window.location.search.substr(1);
    const arrSearch = search.split('=');
    const params = new URLSearchParams(window.location.search);

    const codeId = params.get('code_id');
    const sourceId = codeId ? params.get('id') : arrSearch[1];

    if (sourceId !== undefined && sourceId !== null) {
      store.setParameterId(sourceId);
    }

    if (codeId) {
      store.setCodeId(codeId);
    }

    datadogLogs.logger.info('Page Open', { data: { source_id: sourceId } });
  }

  render() {
    const { store } = this.props;
    let sourceElement = null;
    let paymentPage = null;

    if (store.source.id === '') {
      sourceElement = (
        <p className="SubHead">
          Source&nbsp;
          <span>{store.parameterId}</span>
          &nbsp;is an invalid source.
        </p>
      );
    } else if (!['', 'chargeable', 'pending'].includes(store.source.status)) {
      if ([...CARD_PAYMENT_METHODS, 'three_d_secure'].includes(store.source.type)) {
        const { source } = store;

        let authStatus = '';
        let note = '';
        let { status } = source;

        if (source.type === 'card') {
          // Specific for Cybersource 3DS 2.0
          status = source.threeDSecureStatus || source.status;
        }

        if (status === 'consumed') {
          authStatus = 'Authentication Complete.';
        } else {
          authStatus = 'Authentication failed or cancelled.';
        }

        if (store.willRedirectToNewPage) {
          note = 'You will be redirected in a few seconds...';
        } else {
          note = 'You may now close this window.';
        }

        sourceElement = (
          <div className="Message">
            <p className="SubHead">{authStatus}</p>
            <p>{note}</p>
          </div>
        );
      } else {
        sourceElement = (
          <>
            <p className="SubHead">
              Source&nbsp;
              <span>{store.source.id}</span>
              &nbsp;has&nbsp;
              <span>{store.source.status}</span>
              &nbsp;status
            </p>
            {store.source.status === 'consumed' && store.source.type === 'paymaya' && (
              <p className="Message">
                Authentication Done. You will be redirected in a few seconds.
              </p>
            )}
          </>
        );
      }
    } else if (store.source.status === 'pending') {
      if (store.livemode === true) {
        if (store.source.redirect.provider === 'robinsons_bank') {
          paymentPage = <RobinsonsBankPaymentPageComponent />;
        } else if (store.source.redirect.provider === 'sathapana_bank') {
          paymentPage = <SathapanaBankPaymentPageComponent />;
        } else {
          switch (store.source.type) {
            case 'card':
              // Will validate and submit customer details for Cybersource 3DS 2.0
              paymentPage = <CardDeviceDataCollectionComponent />;
              break;
            case 'google_pay_card':
              // Will validate and submit customer details for Cybersource 3DS 2.0
              paymentPage = <CardDeviceDataCollectionComponent />;
              break;
            default:
              // Will go to our live payment page for Cybersource 3DS 1.0
              paymentPage = <LivePaymentPageComponent />;
              break;
          }
        }
      } else {
        paymentPage = <TestPaymentPageComponent />;
      }
    } else if (
      store.source.status === 'chargeable' &&
      CARD_PAYMENT_METHODS.includes(store.source.type)
    ) {
      paymentPage = <LivePaymentPageComponent />;
    } else if (
      store.source.type === 'qrph' &&
      store.source.status === 'chargeable' &&
      store.livemode === false
    ) {
      paymentPage = <TestPaymentPageComponent />;
    }

    return (
      <div className="MainScreen">
        <div className="Header">
          {store.livemode === false && (
            <img src={paymongoImage} alt="Paymongo" className="MainScreen__logo" />
          )}
        </div>
        <div className="Content">
          <LoaderComponent spinning={store.pageLoading} text={store.pageLoadingText} />
          {sourceElement}
          {paymentPage}
        </div>
        <div />
      </div>
    );
  }
}

MainScreen.propTypes = {
  store: PropTypes.shape({
    livemode: PropTypes.bool,
    loadData: PropTypes.func.isRequired,
    pageLoadingText: PropTypes.string,
    pageLoading: PropTypes.bool.isRequired,
    parameterId: PropTypes.string,
    redirectUrl: PropTypes.string,
    source: PropTypes.shape({
      id: PropTypes.string,
      failedUrl: PropTypes.string,
      redirect: PropTypes.shape({
        provider: PropTypes.string
      }),
      status: PropTypes.string,
      successUrl: PropTypes.string,
      threeDSecureStatus: PropTypes.string,
      type: PropTypes.string
    }).isRequired,
    setPageLoadingText: PropTypes.func.isRequired,
    setPageLoading: PropTypes.func.isRequired,
    setParameterId: PropTypes.func.isRequired,
    setCodeId: PropTypes.func.isRequired,
    setRedirectUrl: PropTypes.func.isRequired,
    willPostMessageToWindow: PropTypes.func.isRequired,
    willRedirectToNewPage: PropTypes.bool.isRequired
  }).isRequired
};

export default inject(stores => {
  return {
    store: stores.store.source
  };
})(observer(MainScreen));
